<template>
  <f7-page class="frm-expedition-new-no-sales-order">
    <template #fixed>
      <TheNavbar
        :nav-title="$t('commercialization.common.newExpeditionNoSalesOrder')"
        back-btn-enable
        class="secondary-navbar"
      />
    </template>

    <!-- Step 1: Search customer -->
    <f7-block>
      <UCSearchEntityERP
        ref="searchClientRef"
        :title="$t('common.customer')"
        :placeholder="$t('common.customer')"
        :search-text="$t('searchEntity.client.searchText')"
        :search-cancellation-text="$t('searchEntity.client.searchCancellationText')"
        :search-not-found-text="$t('searchEntity.client.searchNotFoundText')"
        :procedure-for-search="searchClientsProcedure"
        :property-entity-shown-input="Array('Codigo', 'Sujeto.Nombre')"
        :property-entity-shown-list-title="Array('Codigo')"
        :property-entity-shown-list-subtitle="Array('Sujeto.Nombre')"
        :autofocus-on-deselection="true"
        @selection="onClientSelection"
      />
    </f7-block>

    <!-- Step 2: Search address -->
    <f7-block>
      <UCSearchEntityERP
        ref="searchAddressRef"
        :title="$t('common.address')"
        :placeholder="$t('common.address')"
        :search-text="$t('searchEntity.address.searchText')"
        :search-cancellation-text="$t('searchEntity.address.searchCancellationText')"
        :search-not-found-text="$t('searchEntity.address.searchNotFoundText')"
        :procedure-for-search="searchAddressesProcedure"
        :property-entity-shown-input="Array('Nombre')"
        :property-entity-shown-list-title="Array('Nombre')"
        :property-entity-shown-list-subtitle="Array('Direccion')"
        :autofocus-on-deselection="true"
        :disabled="!selectedClient"
        @selection="onAddressSelection"
      />
    </f7-block>

    <!-- Step 3: Search article -->
    <f7-block>
      <UCSearchEntityERP
        ref="searchProductRef"
        :title="$t('common.article')"
        :placeholder="$t('common.article')"
        :search-text="$t('searchEntity.article.searchText')"
        :search-cancellation-text="$t('searchEntity.article.searchCancellationText')"
        :search-not-found-text="$t('searchEntity.article.searchNotFoundText')"
        :procedure-for-search="searchArticlesProcedure"
        :property-entity-shown-input="Array('Nombre')"
        :property-entity-shown-list-title="Array('Nombre')"
        :property-entity-shown-list-subtitle="Array('Codigo')"
        :autofocus-on-deselection="true"
        @selection="onArticleSelection"
      />
    </f7-block>

    <f7-block>
      <f7-button
        fill
        :disabled="!selectedClient || !selectedAddress || !selectedProduct"
        @click="addLineToList"
      >
        {{ $t('commercialization.common.addLine') }}
      </f7-button>
    </f7-block>

    <f7-block
      v-if="selectedLines && selectedLines.length > 0"
      class="content-cards"
    >
      <div class="list accordion-list content-cards-list">
        <TransitionGroup tag="ul">
          <PCNoSalesOrderNewCard
            v-for="(line, index) in selectedLines"
            :key="index"
            :selected-line="line"
            @delete-line="handleDeleteLine"
          />
        </TransitionGroup>
      </div>
    </f7-block>
    <f7-block
      v-if="!editable"
      class="container-continue"
    >
      <f7-button
        small
        fill
        :disabled="!selectedLines || selectedLines.length < 1"
        @click="navigateToExpeditionDocumentMoreData"
      >
        {{ $t('common.continue') }}
      </f7-button>
    </f7-block>
    <f7-block
      v-else
      class="container-continue"
    >
      <f7-button
        small
        fill
        @click="navigateToExpeditionDocument"
      >
        {{ $t('commercialization.common.addLines').toUpperCase() }}
      </f7-button>
    </f7-block>

    <InfoSheet
      :state="infoSheetParams.state"
      :opened="infoSheetParams.opened"
      :title="infoSheetParams.title"
      :subtitle1="infoSheetParams.subtitle1"
      @closed="infoSheetParams.opened = false"
    />
  </f7-page>
</template>

<script setup>
// @ts-check
import {
  computed,
  onMounted,
  reactive,
  ref,
  shallowRef,
  toRefs,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useAppConfigStore } from '@/js/stores/appConfig';
import { f7 } from 'framework7-vue';
import {
  HiDialog, HiNotification,
} from '@/js/common/HINotificationSupport';
import APIErp from '@/js/services/APIErp';
import { useI18n } from 'vue-i18n';
import { TipoBloqueo } from '@/js/common/enum';
import InfoSheet from '@/components/common/InfoSheet.vue';
import TheNavbar from '@/components/TheNavbar.vue';
import UCSearchEntityERP from '@/components/common/UCSearchEntityERP.vue';
import PCNoSalesOrderNewCard from '@/components/production/commercialization/expedition/PCNoSalesOrderNewCard.vue';

/** @import { Ref, ShallowRef } from 'vue' */

/**
 * @import {
 * Articulo,
 * Cliente,
 * InfoSheetState,
 * SPApp,
 * } from '@erpagroapp/types' */

const props = defineProps({
  expeditionDocument: {
    type: Object,
    default: null,
  },
  editable: {
    type: Boolean,
    default: false,
  },
});

const { expeditionDocument } = toRefs(props);
const { editable } = toRefs(props);
const { t } = useI18n();
const { userEnviroment } = storeToRefs(useAppConfigStore());
/** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
const searchClientRef = shallowRef(null);
/** @type {Ref<Cliente | null>} */
const selectedClient = ref(null);

/** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
const searchAddressRef = shallowRef(null);
/** @type {Ref<Record<string, any> | null>} */
const selectedAddress = ref(null);

/** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
const searchProductRef = shallowRef(null);
/** @type {Ref<Articulo | null>} */
const selectedProduct = ref(null);

/** @type {Ref<Record<string, any>[]>} */
const selectedLines = ref([]);

/** @type {Ref<Record<string, any> | null>} */
const expeditionDocumentSelected = ref(null);

const infoSheetParams = reactive({
  opened: false,
  subtitle1: '',
  title: '',
  /** @type {InfoSheetState} */
  state: 'CAUTION',
});

watch(expeditionDocument, async (newExpeditionDocument) => {
  expeditionDocument.value = newExpeditionDocument;
});

const searchClientsProcedure = computed(() => /** @satisfies {SPApp} */ ({
  NombreSP: 'APPERPAGRO_ClientesXIdGrupoEmpresarial',
  Parametros: [
    { NombreParametro: 'filtro', Valor: 'q:' },
    { NombreParametro: 'idsGrupoEmpresarial', Valor: userEnviroment.value?.EntornoDeTrabajo.GrupoEmpresarial.Id },
    { NombreParametro: 'idEmpresa', Valor: userEnviroment.value?.EntornoDeTrabajo.Empresa?.Id ?? 0 },
    { NombreParametro: 'fechaComprobacionBloqueos', Valor: new Date() },
    { NombreParametro: 'idSujetoVendedor', Valor: 0 },
  ],
  EntidadMapear: 'Comun.ClienteJSON',
}));

const searchAddressesProcedure = computed(() => /** @satisfies {SPApp} */ ({
  NombreSP: 'APPERPAGRO_DireccionesXidSujeto',
  Parametros: [
    { NombreParametro: 'filtro', Valor: 'q:' },
    { NombreParametro: 'idSujeto', Valor: selectedClient.value?.Sujeto?.Id ?? 0 },
  ],
  EntidadMapear: 'DireccionJSON',
}));

const searchArticlesProcedure = computed(() => /** @satisfies {SPApp} */ ({
  NombreSP: 'APPERPAGRO_ArticulosXTipoArticulo',
  Parametros: [
    { NombreParametro: 'filtro', Valor: 'q:' },
    { NombreParametro: 'tipoArticulo', Valor: 2 }, /** 2 = Product */
    { NombreParametro: 'idEmpresa', Valor: userEnviroment.value?.EntornoDeTrabajo.Empresa?.Id ?? 0 },
    { NombreParametro: 'fecha', Valor: new Date() },
    { NombreParametro: 'materiPrimaCheck', Valor: 1 },
  ],
  EntidadMapear: 'Comun.ArticuloJSON',
}));

function StartComponent() {
  if (expeditionDocument.value != null) {
    expeditionDocumentSelected.value = expeditionDocument.value;
  } else {
    expeditionDocumentSelected.value = {
      Serie: '',
      Numero: 0, // Nueva
      Conductor: null,
      EstadoOrdenCarga: 0,
      FechaSalida: null,
      Id: 0,
      Lineas: [],
      Matricula: null,
      Matricula2: null,
      Observaciones: null,
      TotalPales: 0,
      TotalPalesCargados: 0,
      Transportista: null,
      MuelleCarga: null,
      Dni: null,
      Temperatura: null,
    };
  }
}
onMounted(() => {
  StartComponent();
});
/**
 * Handles the article selection.
 * @param {Articulo | null} value - The value.
 */
function onArticleSelection(value) {
  if (!value && selectedProduct.value) {
    selectedProduct.value = value;
    searchProductRef.value?.Clear();
  } else {
    selectedProduct.value = value;
    if (selectedProduct.value) {
      const block = checkBlocks(selectedProduct.value, t('common.article'));
      if (block) {
        searchProductRef.value?.Clear();
      }
    }
  }
}

/**
 * Handles the address selection.
 * @param {Record<string, any> | null} value - The value.
 */
function onAddressSelection(value) {
  if (!value) {
    if (selectedAddress.value) {
      selectedAddress.value = value;
      searchAddressRef.value?.Clear();
    } else {
      selectedAddress.value = value;
    }
  } else {
    selectedAddress.value = value;
  }
}

/**
 * Handles the client selection.
 * @param {Cliente | null} value - The value.
 */
function onClientSelection(value) {
  if (!value) {
    if (selectedClient.value) {
      selectedClient.value = value;
      searchClientRef.value?.Clear();
    } else {
      selectedClient.value = value;
    }
    if (selectedAddress.value) {
      onAddressSelection(null);
    }
  } else {
    selectedClient.value = value;
    const block = checkBlocks(selectedClient.value, t('common.customer'));
    if (block) {
      searchClientRef.value?.Clear();
    }
  }
}
/**
 * @param {Articulo | Cliente} item
 * @param {string} itemType
 * @returns {boolean} Whether the item is blocked.
 */
function checkBlocks(item, itemType) {
  let hasBlocking = false;
  let tipeOutOfUse = null;
  let tipeBlock = null;
  let tipeWarning = null;

  if (item.Bloqueos) {
    for (const block of item.Bloqueos) {
      if (block.TipoBloqueo === TipoBloqueo.FueraDeUso) {
        tipeOutOfUse = block;
      } else if (block.TipoBloqueo === TipoBloqueo.Bloqueo) {
        tipeBlock = block;
      } else if (block.TipoBloqueo === TipoBloqueo.Aviso) {
        tipeWarning = block;
      }
    }
  }

  if (tipeOutOfUse) {
    hasBlocking = true;
    infoSheetParams.opened = true;
    infoSheetParams.subtitle1 = tipeOutOfUse.Motivo ?? '';
    infoSheetParams.title = `${itemType}: ${t('common.outOfUse')}`;
    infoSheetParams.state = 'ERROR';
  } else if (tipeBlock) {
    hasBlocking = true;
    infoSheetParams.opened = true;
    infoSheetParams.subtitle1 = tipeBlock.Motivo ?? '';
    infoSheetParams.title = `${itemType}: ${t('common.block')}`;
    infoSheetParams.state = 'ERROR';
  } else if (tipeWarning) {
    infoSheetParams.opened = true;
    infoSheetParams.subtitle1 = tipeWarning.Motivo ?? '';
    infoSheetParams.title = `${itemType}: ${t('common.warning')}`;
    infoSheetParams.state = 'CAUTION';
  }

  return hasBlocking;
}

function addLineToList() {
  if (!selectedClient.value
    || !selectedAddress.value
    || !selectedProduct.value) {
    return;
  }

  selectedLines.value.push({
    Id: 0,
    TemporalId: selectedLines.value.length + 1,
    Cliente: selectedClient.value,
    Direccion: selectedAddress.value,
    Producto: selectedProduct.value,
    CantidadTotalPales: 1,
    Desglose: [],
  });

  // Reset all fields
  onClientSelection(null);
  onArticleSelection(null);
}

/**
 * Handles the delete line.
 * @param {Record<string, any>} line - The line.
 */
function handleDeleteLine(line) {
  const index = selectedLines.value.findIndex((l) => l.TemporalId === line.TemporalId);

  if (index !== -1) {
    selectedLines.value.splice(index, 1);
  }
}
function generateDesgloses() {
  expeditionDocumentSelected.value?.Lineas.forEach((l) => {
    for (let index = l.Desglose.length; index < l.CantidadTotalPales; index += 1) {
      l.Desglose.push({
        Estado: 0,
        Id: 0,
        UnidadContenedora: null,
      });
    }
  });
}

function navigateToExpeditionDocumentMoreData() {
  if (expeditionDocumentSelected.value) {
    expeditionDocumentSelected.value.Lineas = [...selectedLines.value];
  }
  generateDesgloses();
  f7.views.main.router.navigate('/commercialisation/expedition/noSalesOrder/frmExpeditionDocumentNoSalesCompleteData', {
    props: {
      expeditionDocument: expeditionDocumentSelected,
      returnToNoSalesOrder: true,
    },
  });
}
/**
 * @param {Record<string, any> | null} objExpedition
 */
async function SaveExpedition(objExpedition) {
  HiDialog.Preloader(true, 'Editando orden de carga', false);
  try {
    const result = await APIErp.callSaveExpeditionNoSales(objExpedition);
    HiDialog.Preloader(false);
    return result.data;
  } catch (error) {
    HiDialog.Preloader(false);
    const typedError = /** @type {import('axios').AxiosError} */ (error).response?.data.Mensaje;
    if (typedError) {
      HiNotification.Alert(typedError, 'Aviso');
    }
    return null;
  }
}
async function navigateToExpeditionDocument() {
  if (expeditionDocumentSelected.value) {
    if (!expeditionDocumentSelected.value?.Lineas) {
      expeditionDocumentSelected.value.Lineas = [];
    }
    selectedLines.value.forEach((element) => {
      expeditionDocumentSelected.value?.Lineas.push(element);
    });
  }

  generateDesgloses();
  // Guardado de expedición
  const resultSave = await SaveExpedition(expeditionDocumentSelected.value);
  if (resultSave == null) { return; }
  f7.views.main.router.navigate('/commercialisation/expedition/noSalesOrder/frmExpeditionDocumentNoSales', {
    props: {
      expeditionDocument: expeditionDocumentSelected,
      reload: true,
    },
  });
}
</script>

<style lang="scss">
/* .page-content{
    overflow-y: hidden;
  }  */
.frm-expedition-new-no-sales-order {
  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }

  .content-cards {
    overflow: scroll;
    height: calc(100vh - 440px);
    padding-bottom: var(--information-bar-height);
  }

  .container-continue {
    width: 100%;
    position: fixed;
    bottom: var(--information-bar-height);
  }
}
</style>
